import {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
  companyIdSelector,
  selectCompany,
} from '../../store/Slices/companySlice';
import {apiGetCompany} from '../../Service/companyService';
import DialogApiStatus from '../../components/dialogs/DialogApiStatus';
import {REDIRECT_TO_PARAM, sanitizeRedirectTo} from '../../utils/redirectTo';

export default function ImpersonateCompany() {
  const {companyId} = useParams();
  const [apiState, setApiState] = useState({status: '', error: null});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const selectedCompanyId = useSelector(companyIdSelector);

  const fetchCompany = useCallback(() => {
    setApiState({status: 'pending', error: null});
    if (companyId === undefined) {
      return navigate('/');
    }

    // `redirectTo` reaches us from outside the app (the Next-Gen Player hands
    // the link to the system browser), so it is narrowed to a site-relative
    // path before it is ever navigated to.
    const redirectTo = sanitizeRedirectTo(searchParams.get(REDIRECT_TO_PARAM));
    const requestedCompanyId = Number(companyId);

    if (!Number.isInteger(requestedCompanyId) || requestedCompanyId <= 0) {
      return navigate('/');
    }

    // The session already holds this company — either a partner user scoped to
    // their own company, or a staff user still impersonating it. Re-fetching
    // buys nothing, and for a partner user without the `partner-impersonate`
    // scope it fails and strands them on the error dialog instead of letting
    // them through to the page they asked for.
    if (selectedCompanyId === requestedCompanyId) {
      return navigate(redirectTo, {replace: true});
    }

    apiGetCompany(requestedCompanyId)
      .then(cmpny => {
        dispatch(selectCompany(cmpny));
        navigate(redirectTo, {replace: true});
      })
      .catch(error => {
        setApiState({status: 'error', error});
      });
  }, [companyId, dispatch, navigate, searchParams, selectedCompanyId]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  return (
    <DialogApiStatus
      apiState={apiState}
      pendingTitle='Fetching company details'
      pendingSubTitle='Please wait...'
      successTitle=''
      errorTitle='Fetching company details is failed!'
      onRetry={fetchCompany}
    />
  );
}
